import { v4 as uuidv4 } from 'uuid'
import { setToken } from '../../api/client'
import type {
  AuthUser,
  GameStatePayload,
  Plan,
  Subscription,
} from '../../types/auth'
import { FREE_INITIAL_DIAMONDS } from '../../types/auth'
import type { AppRepository } from './types'

const MOCK_USER_KEY = 'numgame_mock_user'
const MOCK_GAME_KEY = 'numgame_mock_game'

function mockPlan(): Plan {
  return import.meta.env.VITE_MOCK_PLAN === 'pro' ? 'pro' : 'free'
}

function buildSubscription(plan: Plan): Subscription {
  return {
    plan,
    billingInterval: plan === 'pro' ? 'month' : null,
    status: 'active',
    currentPeriodEnd: plan === 'pro' ? Date.now() + 30 * 24 * 60 * 60 * 1000 : null,
    cancelAtPeriodEnd: false,
  }
}

function readMockUser(): AuthUser | null {
  const raw = localStorage.getItem(MOCK_USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}

function writeMockUser(user: AuthUser | null): void {
  if (user) localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user))
  else localStorage.removeItem(MOCK_USER_KEY)
}

function defaultGameState(): GameStatePayload {
  return {
    mode: 'endless',
    currentLevel: 1,
    totalScore: 0,
    currentScore: 0,
    highestNumber: 0,
    isLevelCompleted: false,
    moves: 0,
    tools: [],
    language: 'en',
    diamonds: FREE_INITIAL_DIAMONDS,
    soundOn: true,
  }
}

function readGameState(): GameStatePayload {
  const raw = localStorage.getItem(MOCK_GAME_KEY)
  if (!raw) {
    const s = defaultGameState()
    localStorage.setItem(MOCK_GAME_KEY, JSON.stringify(s))
    return s
  }
  try {
    return { ...defaultGameState(), ...(JSON.parse(raw) as GameStatePayload) }
  } catch {
    return defaultGameState()
  }
}

function writeGameState(s: GameStatePayload): void {
  localStorage.setItem(MOCK_GAME_KEY, JSON.stringify(s))
}

function ensureDemoUser(): AuthUser {
  const existing = readMockUser()
  if (existing) {
    const plan = mockPlan()
    const next = { ...existing, plan, subscription: buildSubscription(plan) }
    writeMockUser(next)
    return next
  }
  const plan = mockPlan()
  const user: AuthUser = {
    id: 'mock-user-demo',
    email: 'demo@numgame.local',
    createdAt: Date.now(),
    language: null,
    plan,
    subscription: buildSubscription(plan),
  }
  writeMockUser(user)
  setToken('mock-token')
  return user
}

export const mockRepository: AppRepository = {
  auth: {
    register: async ({ email, password, confirmPassword }) => {
      if (password !== confirmPassword) throw new Error('两次输入的密码不一致')
      if (password.length < 8) throw new Error('密码至少 8 位')
      const plan = mockPlan()
      const user: AuthUser = {
        id: uuidv4(),
        email: email.trim().toLowerCase(),
        createdAt: Date.now(),
        language: null,
        plan,
        subscription: buildSubscription(plan),
      }
      writeMockUser(user)
      writeGameState(defaultGameState())
      setToken('mock-token')
      return { token: 'mock-token', user }
    },

    login: async ({ email, password }) => {
      const normalized = email.trim().toLowerCase()
      if (normalized === 'demo@numgame.local' && password === 'demo1234') {
        const user = ensureDemoUser()
        return { token: 'mock-token', user }
      }
      const existing = readMockUser()
      if (existing && existing.email === normalized) {
        setToken('mock-token')
        return { token: 'mock-token', user: existing }
      }
      const plan = mockPlan()
      const user: AuthUser = {
        id: uuidv4(),
        email: normalized,
        createdAt: Date.now(),
        language: null,
        plan,
        subscription: buildSubscription(plan),
      }
      writeMockUser(user)
      setToken('mock-token')
      return { token: 'mock-token', user }
    },

    me: async () => ensureDemoUser(),

    updateLanguage: async language => {
      const user = ensureDemoUser()
      const next: AuthUser = { ...user, language }
      writeMockUser(next)
      return next
    },

    logout: async () => {
      writeMockUser(null)
      setToken(null)
    },
  },

  subscriptions: {
    getMine: async () => ensureDemoUser().subscription,
    createCheckout: async interval => {
      const user = ensureDemoUser()
      const next: AuthUser = {
        ...user,
        plan: 'pro',
        subscription: {
          plan: 'pro',
          billingInterval: interval,
          status: 'active',
          currentPeriodEnd: Date.now() + (interval === 'year' ? 365 : 30) * 24 * 60 * 60 * 1000,
          cancelAtPeriodEnd: false,
        },
      }
      writeMockUser(next)
      const gs = readGameState()
      writeGameState({ ...gs, diamonds: gs.diamonds + 100 })
      return { url: '/billing?mock_upgraded=1' }
    },
    createPortal: async () => ({ url: '/billing?mock_portal=1' }),
  },

  gameState: {
    get: async () => readGameState(),
    put: async state => {
      const prev = readGameState()
      let diamonds = state.diamonds
      if (diamonds > prev.diamonds + 20) diamonds = prev.diamonds + 20
      if (diamonds < 0) diamonds = 0
      const next = { ...state, diamonds }
      writeGameState(next)
      return next
    },
    getRank: async () => {
      const gs = readGameState()
      const user = ensureDemoUser()
      return [
        {
          rank: 1,
          userId: user.id,
          email: user.email.replace(/(.{2}).+(@.+)/, '$1***$2'),
          totalScore: gs.totalScore,
          highestNumber: gs.highestNumber,
        },
      ]
    },
  },
}
